import { useCallback, useMemo } from 'react';
import { getCollapsedDayLeftPx } from '../utils/getCollapsedDayLeftPx';
import { getVisibleDayColumns } from '../utils/getVisibleDayColumns';
import {
  DAY_CELL_HORIZONTAL_PADDING_PX,
  DAY_WIDTH_PX
} from '../utils/monthlyLayoutConstants';
import type { DayColumnDescriptor, MonthDescriptor } from '../utils/types';

export function useMonthlyTimelineLayout(
  months: MonthDescriptor[],
  expandedMonths: string[],
  isCompact: boolean
) {
  const dayColumns = useMemo<DayColumnDescriptor[]>(() => {
    const columns: DayColumnDescriptor[] = [];
    let globalColumn = 1;

    months.forEach((month, monthIndex) => {
      const isExpanded = expandedMonths.includes(month.key);
      const visibleDays = getVisibleDayColumns(
        month.daysInMonth,
        isExpanded,
        isCompact
      );

      visibleDays.forEach(day => {
        columns.push({
          key: `${month.key}-${day}`,
          monthKey: month.key,
          monthIndex,
          day,
          globalColumn
        });
        globalColumn += 1;
      });
    });

    return columns;
  }, [months, expandedMonths, isCompact]);

  const columnByMonthAndDay = useMemo(() => {
    const map = new Map<string, number>();
    dayColumns.forEach(column => {
      map.set(`${column.monthKey}-${column.day}`, column.globalColumn);
    });
    return map;
  }, [dayColumns]);

  const monthVisibleDays = useMemo(() => {
    const map = new Map<string, number[]>();
    months.forEach(month => {
      const isExpanded = expandedMonths.includes(month.key);
      map.set(
        month.key,
        getVisibleDayColumns(month.daysInMonth, isExpanded, isCompact)
      );
    });
    return map;
  }, [months, expandedMonths, isCompact]);

  const monthFirstGlobalColumn = useMemo(() => {
    const map = new Map<string, number>();
    months.forEach(month => {
      const isExpanded = expandedMonths.includes(month.key);
      const visibleDays = getVisibleDayColumns(
        month.daysInMonth,
        isExpanded,
        isCompact
      );
      const firstDay = visibleDays[0];
      if (firstDay !== undefined) {
        const globalColumn = columnByMonthAndDay.get(
          `${month.key}-${firstDay}`
        );
        if (globalColumn !== undefined) {
          map.set(month.key, globalColumn);
        }
      }
    });
    return map;
  }, [months, expandedMonths, isCompact, columnByMonthAndDay]);

  const getDayLeftPx = useCallback(
    (monthKey: string, day: number): number | null => {
      const month = months.find(entry => entry.key === monthKey);
      if (!month) return null;

      const firstGlobalColumn = monthFirstGlobalColumn.get(monthKey);
      if (firstGlobalColumn === undefined) return null;

      const clampedDay = Math.min(Math.max(day, 1), month.daysInMonth);
      const monthSectionLeftPx = (firstGlobalColumn - 1) * DAY_WIDTH_PX;
      const isExpanded = expandedMonths.includes(monthKey);

      if (isExpanded) {
        return (
          monthSectionLeftPx +
          (clampedDay - 1) * DAY_WIDTH_PX +
          DAY_CELL_HORIZONTAL_PADDING_PX
        );
      }

      const visibleOddDays =
        monthVisibleDays.get(monthKey) ||
        getVisibleDayColumns(month.daysInMonth, false, isCompact);

      return (
        monthSectionLeftPx +
        getCollapsedDayLeftPx(
          clampedDay,
          visibleOddDays,
          DAY_WIDTH_PX,
          DAY_CELL_HORIZONTAL_PADDING_PX
        )
      );
    },
    [
      months,
      monthFirstGlobalColumn,
      expandedMonths,
      monthVisibleDays,
      isCompact
    ]
  );

  return {
    dayColumns,
    gridTemplateColumns: `repeat(${dayColumns.length}, ${DAY_WIDTH_PX}px)`,
    totalColumns: dayColumns.length,
    getDayLeftPx
  };
}
